import type { GeoPoint } from '@lib/dto/geo.dto'
import { isPointInRing } from '@lib/helpers/geo'

/** Candidates across the ring's box, each way, in each of two passes. */
const GRID = 14

/** How much being off-center costs against room to the edges. */
const CENTERING = 0.05

/**
 * Where to write a region's name or number: the point inside its ring
 * farthest from any edge. The middle of an L-shaped area is in the notch,
 * outside it, and a label there names the wrong ground.
 *
 * Found by sampling a grid over the ring's box, then a finer one around the
 * best of those, which is plenty for placing a label. A ring too thin for
 * any sample to land inside gets the average of its corners.
 */
export function labelPoint(ring: readonly GeoPoint[]): GeoPoint {
  const corners = [...ring]
  const average: GeoPoint = {
    latitude:
      corners.reduce((sum, point) => sum + point.latitude, 0) /
      Math.max(corners.length, 1),
    longitude:
      corners.reduce((sum, point) => sum + point.longitude, 0) /
      Math.max(corners.length, 1),
  }
  if (corners.length < 3) return average
  // Degrees of longitude are shorter than degrees of latitude away from the
  // equator; distances are compared with longitude scaled to match.
  const scale = Math.cos((average.latitude * Math.PI) / 180)
  const clearance = (point: GeoPoint): number =>
    Math.min(
      ...corners.map((a, index) => {
        const b = corners[(index + 1) % corners.length] ?? a
        const ax = a.longitude * scale
        const bx = b.longitude * scale
        const px = point.longitude * scale
        const dx = bx - ax
        const dy = b.latitude - a.latitude
        const length = dx * dx + dy * dy
        const t =
          length === 0
            ? 0
            : Math.max(
                0,
                Math.min(
                  1,
                  ((px - ax) * dx + (point.latitude - a.latitude) * dy) /
                    length,
                ),
              )
        return Math.hypot(
          px - (ax + t * dx),
          point.latitude - (a.latitude + t * dy),
        )
      }),
    )

  const search = (
    south: number,
    west: number,
    north: number,
    east: number,
    best: { point: GeoPoint; score: number } | null,
  ): { point: GeoPoint; score: number } | null => {
    let found = best
    for (let row = 0; row < GRID; row += 1) {
      for (let column = 0; column < GRID; column += 1) {
        const point: GeoPoint = {
          latitude: south + ((row + 0.5) / GRID) * (north - south),
          longitude: west + ((column + 0.5) / GRID) * (east - west),
        }
        if (!isPointInRing(point, corners)) continue
        // Down the middle of a long strip every point is as far from the
        // edges; a little pull toward the corners' average centers it.
        const score =
          clearance(point) -
          CENTERING *
            Math.hypot(
              (point.longitude - average.longitude) * scale,
              point.latitude - average.latitude,
            )
        if (found === null || score > found.score) {
          found = { point, score }
        }
      }
    }
    return found
  }

  const latitudes = corners.map((point) => point.latitude)
  const longitudes = corners.map((point) => point.longitude)
  const south = Math.min(...latitudes)
  const north = Math.max(...latitudes)
  const west = Math.min(...longitudes)
  const east = Math.max(...longitudes)
  const coarse = search(south, west, north, east, null)
  if (coarse === null) return average
  const cellLatitude = (north - south) / GRID
  const cellLongitude = (east - west) / GRID
  const fine = search(
    coarse.point.latitude - cellLatitude,
    coarse.point.longitude - cellLongitude,
    coarse.point.latitude + cellLatitude,
    coarse.point.longitude + cellLongitude,
    coarse,
  )
  return (fine ?? coarse).point
}
